import { Capacitor } from "@capacitor/core";
import { ImgID } from "../../image_loader/img_id.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { hapticsController } from "../../haptics/haptics_controller.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { VersionLoader } from "../../version_loader.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { SettingsToggle } from "../common_ui_elements/settings_toggle.js";
import { VibrationStrengthSlider } from "../common_ui_elements/vibration_strength_slider.js";
import { SegmentedControl } from "../common_ui_elements/segmented_control.js";
import { GRAPHICS_QUALITY_ORDER, GraphicsSettings } from "../../graphics/graphics_quality.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { FeedbackDialog } from "./feedback_dialog.js";
import { Vector2 } from "../../common/vector2.js";

export class SettingsDialog extends AbstractDialog
{
  // Design px of the Figma frame (dialog 909 wide), Android layout. Web has no vibration
  // section, so everything below it moves up by VIBRATION_SECTION_H.
  private static readonly DESIGN_W = 909;
  private static readonly VIBRATION_SECTION_H = 216;

  private close_btn!: UIImage;
  private vibration_value_text!: UITextElement;

  constructor()
  {
    const design_h = 1679;
    const design_divider_audio_y = 291;
    const design_divider_vibration_y = 562;
    const design_divider_graphics_y = 778;
    const design_divider_bottom_y = 1002;

    const has_vibration = Capacitor.getPlatform() === "android";
    // Everything below the vibration section moves by this.
    const shift = has_vibration ? 0 : -SettingsDialog.VIBRATION_SECTION_H;

    const body_w = LayoutScaler.widthFromDesign( SettingsDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( design_h + shift );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground( shift );
    this.createTitle();
    this.createDivider( design_divider_audio_y );

    this.createSoundRow();
    this.createMusicRow();
    this.createDivider( design_divider_vibration_y );

    if ( has_vibration )
    {
      this.createVibrationSection();
      this.createDivider( design_divider_graphics_y );
    }

    this.createGraphicsSection( shift );
    this.createDivider( design_divider_bottom_y + shift );

    this.createSupportButton( shift );
    this.createPrivacyButton( shift );
    this.createTermsButton( shift );
    this.createCloseButton();
    this.createVersionRow( shift );
  }

  // XD: drop shadow X:0 Y:3 blur:5 — only the shadow itself is blurred (canvas
  // shadowBlur), the glyphs stay crisp. Shared by title/Sounds/Music/Support/Terms/Privacy.
  private textShadowOptions(): { shadow_blur: number; shadow_offset_x: number; shadow_offset_y: number }
  {
    return {
      shadow_blur: LayoutScaler.widthFromDesign( 5 ),
      shadow_offset_x: LayoutScaler.xFromDesign( 0 ),
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
    };
  }

  // The only background in the dialog: the dark blue capsule image. Everything outside
  // it (the close-button strip at the top, and the version row below) stays fully
  // transparent. UINineSliceElement stretches its middle, so only the height differs
  // between the web and Android layouts.
  private createBackground( shift: number ): void
  {
    const design_bg_y = 88;
    const design_bg_h = 1511;

    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( design_bg_y ),
      LayoutScaler.widthFromDesign( SettingsDialog.DESIGN_W ),
      LayoutScaler.heightFromDesign( design_bg_h + shift ),
      ImgID.UI_CapsuleBlue,
      "settings_dialog_capsule_bg"
    );
    this.addChild( bg );
  }

  private createTitle(): void
  {
    const design_title_y = 165;
    const design_title_h = 109;
    const design_font_size = 80;
    const design_outline_w = 14;

    const title_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( design_title_y ),
      LayoutScaler.widthFromDesign( SettingsDialog.DESIGN_W ),
      LayoutScaler.heightFromDesign( design_title_h ),
      {
        localization_key: "SETTINGS",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
        ...this.textShadowOptions(),
      }
    );
    this.addChild( title_text );
  }

  private createDivider( design_y: number ): void
  {
    const design_divider_x = 186.5;
    const design_divider_w = 547;
    const design_divider_h = 11;

    const divider = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_divider_x ),
      LayoutScaler.yFromDesign( design_y ),
      LayoutScaler.widthFromDesign( design_divider_w ),
      LayoutScaler.heightFromDesign( design_divider_h ),
      ImgID.UI_SettingsDialogDivider
    );
    this.addChild( divider );
  }

  // Icons are 81 px boxes; their art is not centred in the box, so x/y are per icon.
  private createIcon( design_x: number, design_y: number, img_id: ImgID ): void
  {
    const design_icon_size = 81;

    this.addChild( new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      LayoutScaler.widthFromDesign( design_icon_size ),
      LayoutScaler.heightFromDesign( design_icon_size ),
      img_id
    ) );
  }

  /** A row label (Sounds/Music/Vibration/Graphics) vertically centred on `design_center_y`. */
  private createLabel( design_center_y: number, design_w: number, localization_key: string ): void
  {
    const design_label_x = 304;
    const design_label_h = 70;
    const design_font_size = 40;

    this.addChild( new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_label_x ),
      LayoutScaler.yFromDesign( design_center_y - design_label_h / 2 ),
      LayoutScaler.widthFromDesign( design_w ),
      LayoutScaler.heightFromDesign( design_label_h ),
      {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        ...this.textShadowOptions(),
      }
    ) );
  }

  private createSoundRow(): void
  {
    const design_icon_x = 189.5;
    const design_icon_y = 334;
    const design_label_center_y = 381.5;
    const design_toggle_y = 345.5;

    this.createIcon( design_icon_x, design_icon_y, ImgID.UI_SettingsSoundIcon );
    this.createLabel( design_label_center_y, 258, "SETTINGS.SOUNDS" );
    this.createToggle( design_toggle_y, audioController.is_sounds_enabled, v =>
    {
      void audioController.enableSounds( v );
    } );
  }

  private createMusicRow(): void
  {
    const design_icon_x = 184;
    const design_icon_y = 457;
    const design_label_center_y = 503.5;
    const design_toggle_y = 467.5;

    this.createIcon( design_icon_x, design_icon_y, ImgID.UI_SettingsMusicIcon );
    this.createLabel( design_label_center_y, 258, "SETTINGS.MUSIC" );
    this.createToggle( design_toggle_y, audioController.is_music_enabled, v =>
    {
      void audioController.enableMusic( v );
    } );
  }

  private createToggle( design_y: number, initial_state: boolean, on_toggle: ( v: boolean ) => void ): void
  {
    const design_toggle_x = 582;
    const design_toggle_w = 152;
    const design_toggle_h = 68;

    const toggle = new SettingsToggle(
      this.ctx,
      LayoutScaler.xFromDesign( design_toggle_x ),
      LayoutScaler.yFromDesign( design_y ),
      LayoutScaler.widthFromDesign( design_toggle_w ),
      LayoutScaler.heightFromDesign( design_toggle_h ),
      {
        initial_state,
        on_toggle,
      }
    );
    this.addChild( toggle );
  }

  // Its own section (icon + "Вібрація" header + live percentage, then a big drag
  // slider below) instead of a row sharing the Sounds/Music toggle footprint - a
  // real drag target needs far more room than the ~66px-tall toggle track ever had.
  private createVibrationSection(): void
  {
    const design_icon_x = 184.5;
    const design_icon_y = 590;
    const design_label_center_y = 636.5;
    const design_label_w = 299.5;
    const design_value_x = 603.5;
    const design_value_w = 130;
    const design_value_h = 70;
    const design_value_font_size = 38.5;
    const design_slider_x = 180.5;
    const design_slider_y = 690.5;
    const design_slider_w = 556;
    // VibrationStrengthSlider re-derives its actual height from width to match
    // vibrations_progress_bar.png's own aspect ratio (its 5 dots must stay circular,
    // not squashed) - this is only the space reserved for it in the layout.
    const design_slider_h = 100;

    this.createIcon( design_icon_x, design_icon_y, ImgID.UI_SettingsVibrationIcon );
    this.createLabel( design_label_center_y, design_label_w, "SETTINGS.VIBRATION" );

    this.vibration_value_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_value_x ),
      LayoutScaler.yFromDesign( design_label_center_y - design_value_h / 2 ),
      LayoutScaler.widthFromDesign( design_value_w ),
      LayoutScaler.heightFromDesign( design_value_h ),
      {
        text: this.vibrationPercentLabel( hapticsController.getStrength() ),
        font_size: LayoutScaler.textSizeFromDesign( design_value_font_size ),
        font_weight: 800,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Right,
        vertical_align: MyTextVerticalAlign.Center,
        ...this.textShadowOptions(),
      }
    );
    this.addChild( this.vibration_value_text );

    const slider = new VibrationStrengthSlider(
      this.ctx,
      LayoutScaler.xFromDesign( design_slider_x ),
      LayoutScaler.yFromDesign( design_slider_y ),
      LayoutScaler.widthFromDesign( design_slider_w ),
      LayoutScaler.heightFromDesign( design_slider_h ),
      {
        initial_value: hapticsController.getStrength(),
        on_change: ( value ) =>
        {
          hapticsController.previewStrength( value );
          this.vibration_value_text.setText( this.vibrationPercentLabel( value ) );
          if ( value > 0 )
            void hapticsController.vibratePreview();
        },
        on_commit: ( value ) =>
        {
          hapticsController.setStrength( value );
        },
      }
    );
    this.addChild( slider );
  }

  private vibrationPercentLabel( value: number ): string
  {
    // Resolved once rather than via localization_key: the same element also shows plain
    // percentages, and the language can't change while this dialog is open.
    if ( value <= 0 )
      return LocalizationManager.getInstance().getText( LocalizationManager.getCurrentLanguage(), "OFF" );

    return `${Math.round( value * 100 )}%`;
  }

  private createGraphicsSection( shift: number ): void
  {
    const design_icon_x = 185.5;
    const design_icon_y = 803.5;
    const design_label_center_y = 852.5;
    const design_label_w = 429.5;
    const design_control_x = 160.5;
    const design_control_y = 909;
    const design_control_w = 618;
    const design_control_h = 77;
    const design_font_size = 30;

    this.createIcon( design_icon_x, design_icon_y + shift, ImgID.UI_SettingsGraphicsIcon );
    this.createLabel( design_label_center_y + shift, design_label_w, "SETTINGS.GRAPHICS" );

    // Pill geometry in px of the track art (618x77), which the control scales itself
    // without rounding. The art has a shadow under it: its capsules span y 1.2..68.8. The
    // three capsules are equal (200.6 wide) but the gaps between them differ (10 px
    // between 1-2, 7 px between 2-3), so their x bounds are fitted to each capsule's own
    // rounded outline, not to the joints. The pill keeps a 7.8 px gap on every side.
    const control = new SegmentedControl(
      this.ctx,
      LayoutScaler.xFromDesign( design_control_x ),
      LayoutScaler.yFromDesign( design_control_y + shift ),
      LayoutScaler.widthFromDesign( design_control_w ),
      LayoutScaler.heightFromDesign( design_control_h ),
      {
        localization_keys: [ "SETTINGS.GRAPHICS.LOW", "SETTINGS.GRAPHICS.MEDIUM", "SETTINGS.GRAPHICS.HIGH" ],
        initial_index: GRAPHICS_QUALITY_ORDER.indexOf( GraphicsSettings.get() ),
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        text_color: ColorScheme.white,
        track_img: ImgID.UI_SettingsGraphicsTrack,
        art_size: [ design_control_w, design_control_h ],
        slot_bounds: [ [ 4.2, 204.8 ], [ 210.2, 410.8 ], [ 413.2, 613.8 ] ],
        capsule_y: [ 1.2, 68.8 ],
        pill_inset: 7.8,
        pill_h: 52,
        on_select: index => GraphicsSettings.set( GRAPHICS_QUALITY_ORDER[index] ),
      }
    );
    this.addChild( control );
  }

  private createSupportButton( shift: number ): void
  {
    const design_btn_x = 200;
    const design_btn_y = 1057.5;
    const design_btn_w = 512;
    const design_btn_h = 169;
    // The mockup's caption sits a little below the button's centre.
    const design_text_dy = 6;
    const design_font_size = 58;
    const design_outline_w = 8;

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y + shift ),
      LayoutScaler.widthFromDesign( design_btn_w ),
      LayoutScaler.heightFromDesign( design_btn_h ),
      ImgID.UI_SettingsSupportBtn,
      undefined,
      true
    );
    btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      DialogManager.openDialog( new FeedbackDialog() );
    } );
    this.addChild( btn );

    const text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( design_text_dy ),
      LayoutScaler.widthFromDesign( design_btn_w ),
      LayoutScaler.heightFromDesign( design_btn_h ),
      {
        localization_key: "SETTINGS.SUPPORT",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 800,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: ColorScheme.settings_support_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        ...this.textShadowOptions(),
      }
    );
    btn.addChild( text, true );
  }

  private createPrivacyButton( shift: number ): void
  {
    const design_btn_y = 1258;
    const design_font_size = 33;

    this.createLinkButton( design_btn_y + shift, design_font_size, "SETTINGS.PRIVACY", "https://match-star.com/policy.html" );
  }

  private createTermsButton( shift: number ): void
  {
    const design_btn_y = 1417;
    const design_font_size = 48;

    this.createLinkButton( design_btn_y + shift, design_font_size, "SETTINGS.TERMS", "https://match-star.com/terms-of-use.html" );
  }

  private createLinkButton( design_y: number, design_font_size: number, localization_key: string, url: string ): void
  {
    const design_btn_w = 634;
    const design_btn_h = 121;
    const design_btn_x = ( SettingsDialog.DESIGN_W - design_btn_w ) / 2;
    const design_min_font_size = 26;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );

    const btn = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_y ),
      btn_w,
      btn_h,
      ImgID.UI_SettingsLinkBtn,
      "settings_link_btn",
      undefined,
      undefined,
      true
    );
    btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      window.open( url, "_blank", "noopener,noreferrer" );
    } );
    this.addChild( btn );

    const MAGIC_NUM_CORRECTION = 0.8;
    const text_w = btn_w * MAGIC_NUM_CORRECTION;

    const text = new UITextElement(
      this.ctx,
      ( btn_w - text_w ) / 2,
      0,
      text_w,
      btn_h,
      {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        line_height: 1,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( design_min_font_size ),
        ...this.textShadowOptions(),
      }
    );
    btn.addChild( text, true );
  }

  // The copy icon and the text form one row centred on the dialog, measured by the text's
  // real width: at fixed x the row drifted right as the version string got longer.
  private createVersionRow( shift: number ): void
  {
    const design_text_y = 1608.5;
    const design_text_w = 480;
    const design_text_h = 69;
    const design_font_size = 48;
    const design_copy_y = 1625;
    const design_copy_w = 38;
    const design_copy_h = 39;
    const design_copy_gap = 26;
    const opacity = 0.61;

    const version = VersionLoader.getVersion();

    const version_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( design_text_y + shift ),
      LayoutScaler.widthFromDesign( design_text_w ),
      LayoutScaler.heightFromDesign( design_text_h ),
      {
        text: `ver: ${version}`,
        auto_wrap: false,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        ...this.textShadowOptions(),
      }
    );
    version_text.setOpacity( opacity );

    const copy_w = LayoutScaler.widthFromDesign( design_copy_w );
    const gap = LayoutScaler.widthFromDesign( design_copy_gap );
    const row_w = copy_w + gap + version_text.getNaturalTextWidth();
    const row_x = ( LayoutScaler.widthFromDesign( SettingsDialog.DESIGN_W ) - row_w ) / 2;
    version_text.setPixelPos( new Vector2( row_x + copy_w + gap, version_text.pixelPosImportant().y ), true );
    this.addChild( version_text );

    const copy_btn = new UIImage(
      this.ctx,
      row_x,
      LayoutScaler.yFromDesign( design_copy_y + shift ),
      copy_w,
      LayoutScaler.heightFromDesign( design_copy_h ),
      ImgID.UI_SettingsDialogCopyBtn,
      undefined,
      true
    );
    copy_btn.setOpacity( opacity );
    copy_btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      void navigator.clipboard?.writeText( version );
    } );
    this.addChild( copy_btn );
  }

  private createCloseButton(): void
  {
    const design_btn_x = 808;
    const design_btn_y = 51;
    const design_btn_size = 114;

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      LayoutScaler.widthFromDesign( design_btn_size ),
      LayoutScaler.heightFromDesign( design_btn_size ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () =>
    {
      this.close();
    } );
    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public static open(): SettingsDialog
  {
    return DialogManager.openPooled( "SettingsDialog", undefined, () => new SettingsDialog() ) as SettingsDialog;
  }

  public override isPoolable(): boolean { return true; }

  public name(): string
  {
    return "SettingsDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
